import { Moment } from "moment-timezone";
import { Fragment, ReactNode, useContext, useEffect, useMemo, useRef, useState } from "react";
import { gql } from "@apollo/client";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client/react";
import { defineMessages, useIntl } from "react-intl";
import {
  IconCalendarPlus,
  IconEdit,
  IconMessageCircleQuestion,
  IconShoppingCartPlus
} from "@tabler/icons-react";
import Skeleton from "react-loading-skeleton";
import { AnyPeriod, castPeriod, castPrice, isotizeDate, toGQLPeriod, toGQLPrice } from "@ct-react/core";
import { LocaleLink, useLocaleFormatter, useLocaleNavigate } from "@ct-react/locale";
import {
  BookingConfig,
  BookingOption,
  calculateBookingOptionWithExtras,
  castBookingConfig,
  castBookingOption,
  isBookingPrice,
  isBookingPriceWithCharge,
  isDiscountedPrice,
  toGQLBookingPrice
} from "@ct-react/calendar";
import { useVisitor } from "@ct-react/visitor";
import {
  AgeCategory,
  CartOptionType,
  Image,
  RentalAccommodationArticleDetails,
  TouristTaxCartOption
} from "@shared/graphql";
import { ArticleType } from "@shared/url";
import { actions, booking, form, pricing, routes } from "../../../i18n/sharable-defs";
import { CustomRentalAccommodationFilterInput, useSearchContext } from "../../../contexts/search";
import { useTools } from "../../../hooks/tools";
import { getEnumKeyByValue } from "../../../tools/enums";
import { StickableContext, Wrapper } from "../../layout/wrapper";
import Button from "../../common/button";
import InfoBox from "../../common/info-box";
import PeriodInput, { PeriodInputHandler } from "./inputs/period";
import RatioImage from "../../../components/medias/ratio-image";
import GuestsInput from "./inputs/guests";
import ContactForm from "../../contact-form";
import SpriteSvg from "../../../components/common/svg";
import "./rental-booking.scss";

const transDefs = defineMessages({
  choice: { id: "article.on-interest.booking.choice", defaultMessage: "Voir les disponibilités" },
  contact: { id: "article.on-interest.booking.contact", defaultMessage: "Contacter l'agence" },
  emptyPeriod: { id: "article.on-interest.booking.empty-period", defaultMessage: "Veuillez choisir vos dates de séjour." },
  emptyGuests: { id: "article.on-interest.booking.empty-guests", defaultMessage: "Veuillez choisir le nombre de participants." },
  onDemand: { id: "article.on-interest.booking.on-demand-help", defaultMessage: "Disponible sur demande uniquement. Faites suite en contactant l'agence directement." }
});

const ALL_OPTIONS_GQL_DATA = gql`
  query BookingOptions($articleId: ID!) {
    config: bookingAccommodationConfig(articleId: $articleId) {
      period starters { startingAt dayOffset enders { endingAt bookable } } blocks
    }
  }
`;

const PRICE_GQL_DATA = gql`
  query BookingPrice($articleId: ID!, $period: DatePeriod!) {
    option: bookingAccommodationPriceDetail(articleId: $articleId, period: $period) {
      period bookable priceOnDemand
      price { amount detailed { bare discount providerCharge { amount adaptive } } }
    }
  }
`;

const ADD_TO_CART_GQL_MUT = gql`
  mutation AddToCart($sessionId: String, $userId: String, $item: AnyAddCartItemInput!, $options: [AnyAddCartItemOptionInput!]) {
    addition: cartAddItem(sessionId: $sessionId, userId: $userId, item: $item, options: $options) {
      cartId
      allowsMultiItems
      item {
        ... on RentalAccommodationCartItem {
          itemId articleId title description image period
          price { bare discount options { type amount } amount }
          downPayment { rate providerCharge } expirationDate
        }
      }
      fullPrice { discount amount }
    }
  }
`;

type Config = {
  articleId: string;
  articleData: Partial<RentalAccommodationArticleDetails>;
  presetPeriod?: AnyPeriod;
  maxGuests?: number;
  touristTaxConfig?: TouristTaxCartOption;
}

type MainProps = {
  config: Config;
  image?: Image
  onChoice?: (value?: AnyPeriod) => void;
}

type ContentProps = Omit<MobileResumeProps, "loading"> & {
  loading: { price: boolean, action: boolean };
  config: Config & { bookingConfig?: BookingConfig<Moment> };
  guests?: Record<AgeCategory, number>;
  image?: Image,
  onPeriodChange: (value?: AnyPeriod) => void;
  onGuestsChange: (value?: Record<AgeCategory, number>) => void;
  onAction: (choice: BookingOption<Moment>) => void;
};

type MobileResumeProps = {
  loading: boolean;
  choice?: BookingOption<Moment>;
}

type PriceDetailProps = MobileResumeProps & { guestsNeeded: boolean };

const PriceDetail = ({ loading, choice, guestsNeeded }: PriceDetailProps) => {

  const intl = useIntl();
  const { print } = useLocaleFormatter();
  const { format } = useTools();

  const taxes = useMemo(() => {
    if (!choice || !choice.bookable || !isBookingPrice(choice.price)) return undefined;
    return choice.price.detailed.extras?.find(ex => ex.type === CartOptionType.TOURIST_TAX);
  }, [ choice ]);

  if (loading)
    return (
      <dl className="booking-price">
        <dt><Skeleton width="65%" /></dt>
        <dd><Skeleton width="120px" /></dd>
        <dt><Skeleton width="65%" /></dt>
        <dd><Skeleton width="120px" /></dd>
    </dl>);

  if (!choice)
    return <InfoBox msg={intl.formatMessage(transDefs.emptyPeriod)} />

  return (
    <>
      {(guestsNeeded && !taxes && choice?.bookable) &&
        <InfoBox className="no-guests"
                 msg={intl.formatMessage(transDefs.emptyGuests)} />
      }
      <dl className="booking-price">
        {isBookingPrice(choice.price) &&
          <>
            <dt>{intl.formatMessage(booking.nights, { count: format.nights(choice.period) })}</dt>
            <dd>{print.price(choice.price.detailed.bare)}</dd>
            {isDiscountedPrice(choice.price) &&
              <>
                <dt>{intl.formatMessage(pricing.discount)}</dt>
                <dd className="discount">{print.price(choice.price.detailed.discount!)}</dd>
              </>
            }
            {!!taxes &&
              <>
                <dt>{`${intl.formatMessage(pricing.taxes)} (${taxes.data.participantsCount.reduce((acc: number, { number }: any) => acc + number, 0)} p.)`}</dt>
                <dd>{print.price(taxes.amount)}</dd>
              </>
            }
            {isBookingPriceWithCharge(choice.price) &&
              <>
                <dt>{intl.formatMessage(pricing.chargeLabel)}</dt>
                <dd>
                  {intl.formatMessage(pricing.charge, {
                    adaptive: choice.price.detailed.providerCharge!.adaptive,
                    amount: print.price(choice.price.detailed.providerCharge!.amount)
                  })}
                </dd>
              </>
            }
          </>
        }
        <dt className="total">{intl.formatMessage(pricing.total)}</dt>
        <dd className="total">
          {choice.price === "onDemand"
            ? intl.formatMessage(pricing.onDemand )
            : print.price(choice.price.amount)
          }
        </dd>
      </dl>
      {choice.price === "onDemand" &&
        <span className="textual-ondemand">{intl.formatMessage(transDefs.onDemand)}</span>
      }
    </>);

}

const MobileResume = ({ loading, choice }: MobileResumeProps) => {

  const intl = useIntl();
  const { print } = useLocaleFormatter();

  if (!choice)
    return (
      <>
        <IconCalendarPlus className="m-icon" />
        <Button className="primary"
                processing={loading}>
          {intl.formatMessage(transDefs.choice)}
        </Button>
      </>);

  return (
    <>
      {choice.bookable
        ? <IconShoppingCartPlus className="m-icon" />
        : <IconMessageCircleQuestion className="m-icon" />
      }
      <div className="full-resume" style={{...loading && { width: "130px" }}}>
        {(() => {
          if (loading)
            return (<>
              <Skeleton containerClassName="amount" width="45%" />
              <Skeleton containerClassName="period" width="100%" />
            </>);
          return (<>
            <span className="amount">
              {choice.price === "onDemand" ? intl.formatMessage(pricing.onDemand) : print.price(choice.price.amount)}
            </span>
            <span className="period">
              {print.period(choice.period, true)}<IconEdit className="e-icon" />
            </span>
          </>);
        })()}
      </div>
    </>);

}

const BookingWrapperLinks = () => {
  const intl = useIntl();
  return(
    <div className="links">
      <LocaleLink to="useful-info" className="btn link with-icon">
        {intl.formatMessage(routes.useful_info)}
        <SpriteSvg id="chevron-right" className="chevron"/>
      </LocaleLink>
      <a href="tel:+41794876948" className="btn link with-icon">
        {intl.formatMessage(actions.callUs)}
        <SpriteSvg id="chevron-right" className="chevron"/>
      </a>
    </div>
  );
}

const Content = (
  {
    loading,
    choice,
    config,
    guests,
    image,
    onAction: parentOnAction,
    onPeriodChange,
    onGuestsChange
  }: ContentProps) => {

  const intl = useIntl();
  const { sticked } = useContext(StickableContext);

  const periodInputRef = useRef<PeriodInputHandler>(null);
  const [ slidedItem, setSliderItem ] = useState<number>(1);

  const actionLabel = useMemo(() => {
    if (!choice) return transDefs.choice;
    return choice.bookable ? actions.book : transDefs.contact;
  }, [ choice ]);

  const actionIsDisabled = useMemo(() => {
    if (!config.touristTaxConfig || !choice || choice.price === "onDemand") return false;
    return !choice?.price.detailed.extras?.find(extra => extra.type === CartOptionType.TOURIST_TAX);
  }, [ choice, config.touristTaxConfig ]);

  const onAction = () => {
    if (!choice) periodInputRef.current?.open();
    else if (!choice.bookable) setSliderItem(2);
    else parentOnAction(choice);
  }
  return (
    <Wrapper.SlidableBox displayedIndex={slidedItem}
                         animated={false}>

      <Wrapper.SlidableItem className="booking-slide-item">
        {(() => {
          const content: Record<string, { title: string, keepOpen?: true, className?: string, element: ReactNode }> = {
            // image: {
            //   title: "",
            //   element: <RatioImage source={image!.assets} className="main-img"/>
            // },
            period: {
              title: intl.formatMessage(booking.period),
              element: <PeriodInput ref={periodInputRef}
                                    config={config.bookingConfig}
                                    value={config.presetPeriod}
                                    onChange={onPeriodChange} />
            },
            ...(!!config.touristTaxConfig) && {
              guests: {
                title: intl.formatMessage(form.guests),
                element: <GuestsInput maxGuests={config.maxGuests}
                                      value={guests}
                                      onChange={onGuestsChange} />
              }
            },
            choice: {
              title: intl.formatMessage(pricing.detailed),
              keepOpen: true,
              className: "details",
              element: <PriceDetail loading={loading.price}
                                    choice={choice}
                                    guestsNeeded={!!config.touristTaxConfig} />
            },
          };

          if (sticked)
            return (
              <Wrapper.Collapse autoClose={true}
                                animated={false}>
                {Object.values(content).map(({ title, keepOpen, className, element }, index) => (
                  <Wrapper.CollapseItem key={index}
                                        className={className}
                                        title={title}
                                        open={index === 0}
                                        keepOpen={keepOpen}>
                    {element}
                  </Wrapper.CollapseItem>)
                )}
              </Wrapper.Collapse>);

          return Object.values(content).map(({ element }, index) => (<Fragment key={index}>{element}</Fragment>));

        })()}

        {!(sticked && !choice) &&
          <Button type="button"
                  className="main-action primary bold with-icon center"
                  processing={loading.action}
                  disabled={actionIsDisabled}
                  onClick={onAction}>
          {intl.formatMessage(actionLabel)}
          <SpriteSvg id="chevron-right" className="icon"/>
          </Button>
        }

        {!sticked && <BookingWrapperLinks/>}
      </Wrapper.SlidableItem>

      <Wrapper.SlidableItem className="contact-slide-item">
        <Button className="back link with-icon" onClick={() => setSliderItem(1)}>
          <SpriteSvg id="chevron-left" className="chevron"/>
          {intl.formatMessage(actions.back)}
        </Button>
        <ContactForm related={{ articleId: config.articleId, ...(!!choice) && { period: choice.period } }} />
      </Wrapper.SlidableItem>

    </Wrapper.SlidableBox>);

};

const RentalBookingBox = (
  {
    config,
    image,
    onChoice = () => void 0
  }: MainProps) => {

  const { sessionId, userId } = useVisitor();
  const navigate = useLocaleNavigate();
  const { format, gtm } = useTools();

  const [ { [ArticleType.RENTAL]: { saving: ctx } } ] = useSearchContext();
  const initedGuests = config.touristTaxConfig ? (ctx as CustomRentalAccommodationFilterInput).guests : undefined;

  const [ bookingConfig, setBookingConfig ] = useState<BookingConfig<Moment> | undefined>(undefined);
  const [ choice, setChoice ] = useState<BookingOption<Moment> | undefined>();
  const [ guests, setGuests ] = useState<Record<AgeCategory, number> | undefined>(initedGuests);

  const bookingChoice = useMemo<BookingOption<Moment> | undefined>(() => {
    if (!config.touristTaxConfig || !guests || !choice || choice.price === "onDemand")
      return choice;

    const nights = format.nights(choice.period);
    const nightsHasExtras = [
      {
        type: CartOptionType.TOURIST_TAX,
        amount: {
          amount: Object.entries(guests).reduce((acc, [ ageCategory, count ]) => {
            const byNight = config.touristTaxConfig!.grid.find(gi => gi.ageCategory === ageCategory);
            return acc + (!byNight ? 0 : (castPrice(byNight.price).amount * nights * count));}, 0),
          currency: castPrice(choice.price.amount).currency
        },
        data: {
          optionId: config.touristTaxConfig!.optionId,
          participantsCount: Object.entries(guests).reduce<any>((acc, [ ageCategory, number ]) => {
            acc.push({ ageCategory, number });
            return acc;}, [])
        }
      }
    ];

    return calculateBookingOptionWithExtras(choice, nightsHasExtras) as BookingOption<Moment>;
  }, [ choice, guests ]);

  const { stopPolling, loading: configLoading, data: configData } = useQuery<{ config: BookingConfig }>(ALL_OPTIONS_GQL_DATA, {
    variables: { articleId: config.articleId },
    ssr: false,
    fetchPolicy: "no-cache",
    pollInterval: 30 * 1000
  });

  const [ fetchPrice, { loading: priceLoading } ] = useLazyQuery<any>(PRICE_GQL_DATA, { fetchPolicy: "no-cache" });

  const [ addToCart, { loading: addLoading } ] = useMutation<any>(ADD_TO_CART_GQL_MUT, {
    variables: { sessionId, userId },
    update: (cache, { data }) => {
      if (!data) return;
      const { addition: { cartId, item, allowsMultiItems, fullPrice } } = data;
      cache.modify({
        id: cache.identify({ __ref: `Cart:${cartId}`}),
        fields: {
          // @ts-ignore
          items: (cached: any[] = []) => allowsMultiItems ? [ ...cached, item ] : [ item ],
          itemsCount: (cached: number = 0) => allowsMultiItems ? cached + 1 : 1,
          fullPrice: () => fullPrice
        }
      });
    },
    refetchQueries: [ "UserCart", "CartBadge" ]
  });

  useEffect(() => {
    !!config.presetPeriod && onPeriodChange(config.presetPeriod);
    return () => stopPolling();
  }, []);

  useEffect(() => {
    if (configLoading || !configData?.config) return;
    setBookingConfig(castBookingConfig(configData.config));
  }, [ configLoading, configData ]);

  const onPeriodChange = (period?: AnyPeriod) => {
    onChoice(period);
    if (!period)
      setChoice(undefined);
    else
      fetchPrice({
        variables: {
          articleId: config.articleId,
          period: toGQLPeriod(period)
        }
      }).then(({ data: { option } }) => setChoice(castBookingOption(option)));
  }

  const onAction = (choice: BookingOption<Moment>) => {
    if (!isBookingPrice(choice.price)) return;
    const { extras, ...price } = choice.price.detailed;
    const item = { articleId: config.articleId, period: toGQLPeriod(choice.period), price: toGQLBookingPrice(price) };
    const taxes = extras?.find(extra => extra.type === CartOptionType.TOURIST_TAX);
    addToCart({
      variables: {
        item: { rentalAccommodation: { ...item } },
        ...(!!taxes) && {
          options: [
            { touristTax: { ...taxes.data, price: toGQLPrice(taxes.amount) } }
          ]
        }
      }
    })
      .then(({ data: { addition } }) => {
        const price = castPrice(addition.fullPrice.amount);
        const itemPrice = castPrice(addition.item.price.amount);
        const itemPeriod = castPeriod(addition.item.period);
        gtm.sendEvent({
          event: "add_to_cart",
          ecommerce: {
            article_type: getEnumKeyByValue(ArticleType, ArticleType.RENTAL)?.toLowerCase(),
            value: price.amount,
            currency: price.currency,
            items: [
              {
                item_id: config.articleId,
                item_name: config.articleData.technicalName,
                item_period_start: isotizeDate(itemPeriod.start),
                item_period_end: isotizeDate(itemPeriod.end),
                price: itemPrice.amount,
                quantity: 1
              }
            ]
          }
        })
      })
      .then(() => navigate("/checkout"));
  }

  return (
    <Wrapper.StickableBox className="booking-wrapper"
                          resume={<MobileResume loading={configLoading || priceLoading}
                                                choice={bookingChoice} />}
                          content={<Content loading={{ price: priceLoading, action: addLoading || configLoading }}
                                            choice={bookingChoice}
                                            config={{...config, bookingConfig }}
                                            guests={guests}
                                            image={image}
                                            onAction={onAction}
                                            onPeriodChange={onPeriodChange}
                                            onGuestsChange={setGuests} />} />);

};

export default RentalBookingBox;
